import {
  Outlet,
  createRouter,
  createRoute,
  createRootRoute,
  useLocation,
} from '@tanstack/react-router'
import { SidebarProvider, SidebarTrigger } from './components/ui/sidebar'
import { AppSidebar } from './components/app-sidebar'
import { ThemeProvider } from './components/theme-provider'
import { MapRoute } from './routes/map'
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react'
import { JSX, useEffect } from 'react'
import { AccountPage } from './routes/account'
import { WebSocketProvider } from './api/v0/websocket'
import { useStore } from './store/store'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { SensorsRoute } from './routes/sensors'
import { Clerk } from '@clerk/clerk-js'
import { SensorRoute } from './routes/sensor_id'
import { Toaster } from './components/ui/sonner'
import { PreferencesRoute } from './routes/preferences'
import { DashboardRoute } from './routes/dashboard'
import { ZonesRoute } from './routes/zones'
import { ZoneCreateRoute } from './routes/zone-create'
import { ZoneDetailRoute } from './routes/zone-detail'
import { ZoneEditRoute } from './routes/zone-edit'
import { GeofencesRoute } from './routes/geofences'
import { GeofenceCreateRoute } from './routes/geofence-create'
import { GeofenceDetailRoute } from './routes/geofence-detail'
import { GeofenceEditRoute } from './routes/geofence-edit'
import { GeofenceViolationsRoute } from './routes/geofence-violations'
import { AnalyticsRoute } from './routes/analytics'
import { RouteMetadata } from './components/seo/RouteMetadata'
import { PerformanceMonitor } from './components/seo/PerformanceMonitor'
import { HeatmapHistoryRoute } from './routes/heatmap-history'
import { DetectionReplayRoute } from './routes/detection-replay'
import { FlightRestrictionZonesRoute } from './routes/flight-restriction-zones'
import { FlightRestrictionZoneDetailRoute } from './routes/flight-restriction-zone-detail'
import { FlightRestrictionZoneMapRoute } from './routes/flight-restriction-zone-map'
import { FlightRestrictionZoneViolationsRoute } from './routes/flight-restriction-zone-violations'
import { FlightPlanSubmissionRoute } from './routes/flight-plan-submission'
import { FlightPlansRoute } from './routes/flight-plans'
import { FlightPlanDetailRoute } from './routes/flight-plan-detail'
import { LoginPage } from './routes/login'
import { SubscriptionsRoute } from './routes/subscriptions'
import { PrivacyPolicyRoute } from './routes/privacy-policy'
import { TermsOfUseRoute } from './routes/terms-of-use'
import { SupportPage } from './routes/support'

export const clerkClient = new Clerk(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY)

const AuthenticatedLayout = ({ children }: { children: JSX.Element }) => {
  const store = useStore()
  useEffect(() => {
    const loadClerk = async () => {
      await clerkClient.load()
    }

    const intervalId = setInterval(() => {
      store.reset()
    }, 1000 * 30)

    loadClerk()
    return () => clearInterval(intervalId)
  }, [store])

  return (
    <>
      <SignedIn>
        <ThemeProvider defaultTheme="dark" storageKey="stratos-ui-theme">
          <div className="relative min-h-screen bg-background">
            <main className='bg-background'>
              <SidebarProvider>
                <AppSidebar variant='floating' className='z-20' />
                <main className='p-2 w-full'>
                  <SidebarTrigger variant='outline' className='z-20 relative' />
                  {children}
                </main>
              </SidebarProvider>
            </main>
          </div>
        </ThemeProvider>
      </SignedIn>
      <SignedOut>
        <LoginPage />
      </SignedOut>
    </>
  )
}

const BaseLayout = () => {
  const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  const location = useLocation()

  if (!PUBLISHABLE_KEY) {
    throw new Error("Missing clerk Publishable Key")
  }

  const queryClient = new QueryClient()

  // Routes that don't require authentication
  const publicRoutes = ['/legal/privacy-policy', '/legal/terms-of-use']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  if (isPublicRoute) {
    return (
      <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <ThemeProvider defaultTheme="dark" storageKey="stratos-ui-theme">
              <div className="relative min-h-screen bg-background">
                <main className="bg-background">
                  <PerformanceMonitor />
                  <Outlet />
                </main>
              </div>
            </ThemeProvider>
          </NuqsAdapter>
        </QueryClientProvider>
      </ClerkProvider>
    )
  }

  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} >
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <AuthenticatedLayout>
            <WebSocketProvider>
              <PerformanceMonitor />
              <Outlet />
            </WebSocketProvider>
          </AuthenticatedLayout>
        </NuqsAdapter>
      </QueryClientProvider>
    </ClerkProvider>
  )
}

const rootRoute = createRootRoute({
  component: BaseLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Dashboard - Airspace Monitoring Platform"
        description="Monitor and manage your Airspace fleet with real-time tracking, geofencing, and analytics"
      />
      <DashboardRoute />
    </>
  ),
})

const mapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/map',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Map - Real-time Airspace Tracking"
        description="View real-time Airspace locations and flight paths on an interactive map"
      />
      <MapRoute />
    </>
  ),
})

const sensorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sensors',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Sensors - Airspace Device Management"
        description="Manage and monitor your Airspace sensors and devices"
      />
      <SensorsRoute />
    </>
  ),
})

const sensorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sensors/$sensorId',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Sensor Details - Airspace Device Information"
        description="View detailed information and analytics for your Airspace sensor"
      />
      <SensorRoute />
    </>
  ),
})

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountPage,
})

const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preferences',
  component: PreferencesRoute,
})

// Zone routes
const zonesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zones',
  component: () => (
    <>
      <RouteMetadata
        title="Custom Zones - Uavity Airspace Management"
        description="Create and manage custom airspace zones for drone monitoring and geofencing. Define restricted areas and monitoring boundaries."
        keywords="custom zones, airspace zones, drone monitoring zones, geofencing zones, restricted areas, monitoring boundaries"
        tags={['custom zones', 'airspace management', 'monitoring zones', 'restricted areas', 'boundary management']}
      />
      <ZonesRoute />
    </>
  ),
})

const zoneCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zones/create',
  component: () => (
    <>
      <RouteMetadata
        title="Create Custom Zone - Uavity Airspace Management"
        description="Create a new custom airspace zone for drone monitoring and geofencing. Define boundaries and monitoring parameters."
        keywords="create zone, new airspace zone, custom monitoring zone, zone creation, airspace boundary setup"
        tags={['zone creation', 'airspace setup', 'monitoring configuration', 'boundary definition']}
      />
      <ZoneCreateRoute />
    </>
  ),
})

const zoneDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zones/$zoneId',
  component: () => (
    <>
      <RouteMetadata
        title="Zone Details - Uavity Airspace Management"
        description="View detailed information about a custom airspace zone including boundaries, monitoring status, and activity history."
        keywords="zone details, airspace zone information, monitoring status, zone boundaries, activity history"
        tags={['zone details', 'monitoring status', 'boundary information', 'activity tracking']}
      />
      <ZoneDetailRoute />
    </>
  ),
})

const zoneEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zones/$zoneId/edit',
  component: () => (
    <>
      <RouteMetadata
        title="Edit Zone - Uavity Airspace Management"
        description="Edit custom airspace zone settings including boundaries, monitoring parameters, and configuration options."
        keywords="edit zone, modify airspace zone, zone configuration, boundary editing, monitoring settings"
        tags={['zone editing', 'configuration', 'boundary modification', 'settings update']}
      />
      <ZoneEditRoute />
    </>
  ),
})

// Geofence routes
const geofencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences',
  component: () => (
    <>
      <RouteMetadata
        title="Geofencing - Uavity Airspace Management"
        description="Create and manage custom geofences for airspace monitoring. Set up virtual boundaries and automated alerts for drone activity."
        keywords="geofencing, virtual boundaries, drone monitoring, airspace alerts, automated detection, boundary management"
        tags={['geofencing', 'virtual boundaries', 'automated alerts', 'boundary management', 'drone detection']}
      />
      <GeofencesRoute />
    </>
  ),
})

const geofenceCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/create',
  component: () => (
    <>
      <RouteMetadata
        title="Create Geofence - Uavity Airspace Management"
        description="Create a new geofence for airspace monitoring. Define virtual boundaries and configure automated alerts for drone activity."
        keywords="create geofence, new boundary, virtual fence setup, drone monitoring boundary, automated alerts configuration"
        tags={['geofence creation', 'boundary setup', 'alert configuration', 'monitoring setup']}
      />
      <GeofenceCreateRoute />
    </>
  ),
})

const geofenceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId',
  component: () => (
    <>
      <RouteMetadata
        title="Geofence Details - Uavity Airspace Management"
        description="View detailed information about a geofence including boundaries, monitoring status, and violation history."
        keywords="geofence details, boundary information, monitoring status, violation history, geofence analytics"
        tags={['geofence details', 'monitoring status', 'violation tracking', 'boundary information']}
      />
      <GeofenceDetailRoute />
    </>
  ),
})

const geofenceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId/edit',
  component: () => (
    <>
      <RouteMetadata
        title="Edit Geofence - Uavity Airspace Management"
        description="Edit geofence settings including boundaries, alert configurations, and monitoring parameters."
        keywords="edit geofence, modify boundary, geofence configuration, alert settings, monitoring parameters"
        tags={['geofence editing', 'configuration update', 'boundary modification', 'settings management']}
      />
      <GeofenceEditRoute />
    </>
  ),
})

const geofenceViolationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId/violations',
  component: () => (
    <>
      <RouteMetadata
        title="Geofence Violations - Uavity Airspace Management"
        description="View and manage geofence violations. Track unauthorized drone activity and review incident reports."
        keywords="geofence violations, unauthorized activity, incident reports, violation tracking, security alerts"
        tags={['violations', 'incident tracking', 'security alerts', 'unauthorized activity']}
      />
      <GeofenceViolationsRoute />
    </>
  ),
})

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: () => (
    <>
      <RouteMetadata
        title="Analytics Dashboard - Uavity Airspace Monitoring"
        description="Comprehensive insights into drone activity and system performance. View detailed analytics, sensor performance, and detection patterns."
        keywords="drone analytics, airspace analytics, detection patterns, sensor performance, drone activity insights, aviation data analysis"
        tags={['analytics', 'data insights', 'performance monitoring', 'drone activity', 'sensor analytics']}
      />
      <AnalyticsRoute />
    </>
  ),
})

const heatmapHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics/heatmap',
  component: () => (
    <>
      <RouteMetadata
        title="Heatmap Analytics - Uavity Airspace Monitoring"
        description="Visualize drone activity patterns with interactive heatmap analytics. Identify hotspots and activity trends across your monitored airspace."
        keywords="heatmap analytics, drone activity patterns, airspace visualization, activity hotspots, drone density mapping"
        tags={['heatmap', 'visualization', 'activity patterns', 'spatial analysis', 'drone density']}
      />
      <HeatmapHistoryRoute />
    </>
  ),
})

const detectionReplayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics/detection-replay',
  component: () => (
    <>
      <RouteMetadata
        title="Detection Replay - Uavity Airspace Monitoring"
        description="Replay and analyze historical drone detection data. Review past incidents, track flight patterns, and investigate airspace activity."
        keywords="detection replay, historical data, drone tracking history, incident analysis, flight pattern review"
        tags={['detection replay', 'historical analysis', 'incident review', 'flight patterns', 'data playback']}
      />
      <DetectionReplayRoute />
    </>
  ),
})

const flightRestrictionZonesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zones - Uavity Airspace Management"
        description="Monitor and manage flight restriction zones across your area"
      />
      <FlightRestrictionZonesRoute />
    </>
  ),
})

const flightRestrictionZoneDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Details - Uavity Airspace Management"
        description="View detailed information about a specific flight restriction zone"
      />
      <FlightRestrictionZoneDetailRoute />
    </>
  ),
})

const flightRestrictionZoneMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug/map',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Map - Uavity Airspace Management"
        description="View flight restriction zone on an interactive map"
      />
      <FlightRestrictionZoneMapRoute />
    </>
  ),
})

const flightRestrictionZoneViolationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug/violations',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Violations - Uavity Airspace Management"
        description="View and manage violations for a specific flight restriction zone"
      />
      <FlightRestrictionZoneViolationsRoute />
    </>
  ),
})

const flightPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plan/create',
  component: () => (
    <>
      <RouteMetadata
        title="Create Flight Plan - Uavity Airspace Management"
        description="Plan and submit a new flight plan."
        keywords="create flight plan, drone operations, flight submission"
        tags={['flight planning', 'drone operations']}
      />
      <FlightPlanSubmissionRoute />
    </>
  ),
})

const flightPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plans',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Plans - Uavity Airspace Management"
        description="View and manage your flight plans."
      />
      <FlightPlansRoute />
    </>
  ),
})

const flightPlanDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plans/$flightPlanId',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Plan Details - Uavity Airspace Management"
        description="View detailed information about a flight plan."
      />
      <FlightPlanDetailRoute />
    </>
  ),
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions',
  component: SubscriptionsRoute,
})

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/privacy-policy',
  component: () => (
    <>
      <RouteMetadata
        title="Privacy Policy - Uavity"
        description="Read our privacy policy to understand how we collect, use, and protect your data."
        keywords="privacy policy, data protection, user privacy, data collection, privacy rights"
        tags={['privacy', 'legal', 'data protection', 'user rights']}
      />
      <PrivacyPolicyRoute />
    </>
  ),
})

const termsOfUseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/terms-of-use',
  component: () => (
    <>
      <RouteMetadata
        title="Terms of Use - Uavity"
        description="Read our terms of use to understand the rules and guidelines for using our services."
        keywords="terms of use, legal terms, user agreement, service terms, terms and conditions"
        tags={['legal', 'terms', 'agreement', 'user terms']}
      />
      <TermsOfUseRoute />
    </>
  ),
})

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: () => (
    <>
      <RouteMetadata
        title="Support - Uavity"
        description="Get help and support for Uavity services. Contact our team directly."
        keywords="support, help, contact, assistance, customer service"
        tags={['support', 'help', 'contact']}
      />
      <SupportPage />
    </>
  ),
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  mapRoute,
  sensorsRoute,
  sensorRoute,
  accountRoute,
  preferencesRoute,
  zonesRoute,
  zoneCreateRoute,
  zoneDetailRoute,
  zoneEditRoute,
  geofencesRoute,
  geofenceCreateRoute,
  geofenceDetailRoute,
  geofenceEditRoute,
  geofenceViolationsRoute,
  analyticsRoute,
  heatmapHistoryRoute,
  detectionReplayRoute,
  flightRestrictionZonesRoute,
  flightRestrictionZoneDetailRoute,
  flightRestrictionZoneMapRoute,
  flightRestrictionZoneViolationsRoute,
  flightPlansRoute,
  flightPlanRoute,
  flightPlanDetailRoute,
  subscriptionsRoute,
  privacyPolicyRoute,
  termsOfUseRoute,
  supportRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}